import Vue from 'vue';
import { PlantPerformanceFormStep } from '@/types/forms/formSteps/plantPerformanceFormStep';
import { ApplicationForm } from '@/types/forms/specific/applicationForm';
import {
    getOverallNominalPower,
    isOverallActivePowerWarningEnabled,
} from '../../../utilities/application/plantPerformance';
import {
    isPluginGeneratingPlant,
    isPv,
    isRequestWithoutPlanning,
} from '../common';
import { toggleRequiredValidator } from '@/utilities/toggleRequiredValidator';
import { powerMatchesConnectionRules } from '@/utilities/utils';
import { getOverallActivePowerWithPreExisting } from '@/services/form/utilities/application/powerConnection';
import { InverterDataContent } from '@/api/interfaces/content/form/serviceDriven/inverterDataContent';
import { InverterDataContentSearchByManufacturer } from '@/api/interfaces/content/form/serviceDriven/inverterDataContentSearchByManufacturer';
import { contentServices } from '@/services/contentService';

function getInverters(
    data: InverterDataContentSearchByManufacturer,
): InverterDataContent[] | undefined {
    const content = contentServices.form.content;
    if (!content) {
        return undefined;
    }

    const invertersContent = content?.serviceDrivenContent?.invertersContent;
    if (!invertersContent) {
        return undefined;
    }

    return invertersContent.filter(
        (inverter) => inverter.manufacturerName === data.manufacturerName,
    );
}

export const plantPerformanceInverterRules = async (
    formStep: {
        model: PlantPerformanceFormStep;
        formModel: ApplicationForm;
    } & Vue,
): Promise<void> => {
    const requestWithoutPlanning = isRequestWithoutPlanning(formStep.formModel);
    const pluginGeneratingPlant = isPluginGeneratingPlant(formStep.formModel);

    // Get power including additional input of the next form step,
    // to avoid cases where input of this form step would be invalid by itself, but valid with the input on the following step
    const overallActivePowerWithPreexisting =
        getOverallActivePowerWithPreExisting(
            formStep.formModel.applicationType,
            formStep.model,
            formStep.formModel.powerConnection,
        ) ?? 0;

    for (const module of formStep.model.inverter.inverterModules.items.concat(
        formStep.model.inverterStorage.inverterModules.items,
    )) {
        module.inverterManualInputManufacturerTypeZerezId.disabled =
            pluginGeneratingPlant || requestWithoutPlanning;
        const enableManualInput =
            !module.inverterManualInputManufacturerTypeZerezId.disabled &&
            module.inverterManualInputManufacturerTypeZerezId.value === true;

        module.inverterActivePowerAndApparentPower.activePower.userChangeable =
            pluginGeneratingPlant ||
            enableManualInput ||
            requestWithoutPlanning;
        module.inverterActivePowerAndApparentPower.apparentPower.userChangeable =
            pluginGeneratingPlant ||
            enableManualInput ||
            requestWithoutPlanning;

        module.inverterManufacturerAndModuleType.manufacturer.disabled =
            pluginGeneratingPlant || requestWithoutPlanning;
        module.inverterManufacturerAndModuleType.moduleType.disabled =
            pluginGeneratingPlant || requestWithoutPlanning;
        module.inverterManufacturerAndModuleType.manufacturerText.disabled =
            pluginGeneratingPlant ||
            requestWithoutPlanning ||
            !enableManualInput;
        module.inverterManufacturerAndModuleType.moduleTypeText.disabled =
            pluginGeneratingPlant ||
            requestWithoutPlanning ||
            !enableManualInput;
        module.inverterManufacturerAndModuleType.manufacturerSelect.disabled =
            pluginGeneratingPlant ||
            enableManualInput ||
            requestWithoutPlanning;
        module.inverterManufacturerAndModuleType.moduleTypeSelect.disabled =
            pluginGeneratingPlant ||
            enableManualInput ||
            requestWithoutPlanning;
        module.inverterManufacturerAndModuleType.disabled =
            module.inverterManufacturerAndModuleType.manufacturer.disabled &&
            module.inverterManufacturerAndModuleType.moduleType.disabled;

        module.inverterZerezIdAndNorm.zerezId.disabled =
            pluginGeneratingPlant ||
            enableManualInput ||
            requestWithoutPlanning;
        module.inverterZerezIdAndNorm.norm.disabled =
            pluginGeneratingPlant ||
            enableManualInput ||
            requestWithoutPlanning;
        module.inverterZerezIdAndNorm.normText.disabled =
            pluginGeneratingPlant ||
            enableManualInput ||
            requestWithoutPlanning;
        module.inverterZerezIdAndNorm.disabled =
            module.inverterZerezIdAndNorm.zerezId.disabled &&
            module.inverterZerezIdAndNorm.norm.disabled &&
            module.inverterZerezIdAndNorm.normText.disabled;

        // Make module type select not-user-editable, if no manufacturer is selected
        module.inverterManufacturerAndModuleType.moduleTypeSelect.userChangeable = !!module
            .inverterManufacturerAndModuleType.manufacturerSelect.value;

        // Disable module type options that are not supported by the selected manufacturer
        if (
            !enableManualInput &&
            module.inverterManufacturerAndModuleType.manufacturerSelect.value
        ) {
            const inverters = getInverters({
                manufacturerName:
                    module.inverterManufacturerAndModuleType.manufacturerSelect
                        .value,
            });
            if (
                inverters &&
                module.inverterManufacturerAndModuleType.moduleTypeSelect
                    .options
            ) {
                for (const option of module.inverterManufacturerAndModuleType
                    .moduleTypeSelect.options) {
                    option.disabled = !inverters.some(
                        (inverter) => inverter.moduleType === option.key,
                    );
                }
            }
        }

        toggleRequiredValidator(
            module.inverterZerezIdAndNorm.norm,
            !enableManualInput,
        );
        toggleRequiredValidator(
            module.inverterZerezIdAndNorm.normText,
            !enableManualInput,
        );

        // It's important that dependant fields are checked before, so the error message is only displayed,
        // if the user has actually filled out all fields
        const dependantFieldsHaveValues =
            module.inverterManufacturerAndModuleType.manufacturer.value !==
                null &&
            module.inverterManufacturerAndModuleType.moduleType.value !==
                null &&
            module.inverterZerezIdAndNorm.zerezId.value !== null;
        module.unitCertificateInvalidForInstalledPower.disabled = !(
            // We only need this error message, if the required fields for it are themselves filled in the first place
            (
                dependantFieldsHaveValues &&
                // Input is based on selection, instead of free text input
                !enableManualInput &&
                // Power check match, based on which connections the selected inverter supports
                !powerMatchesConnectionRules(
                    overallActivePowerWithPreexisting,
                    module.inverterZerezIdAndNorm.norm?.value ?? [],
                )
            )
        );
    }

    // https://netzkern.atlassian.net/wiki/spaces/EWE/pages/4864934167/Feldgruppe+Wechselrichter+PV+und+Speicher+MS#FeldgruppeWechselrichterPVundSpeicherMS-overallActivePower
    // if the overall active power differs from the photovoltaic overall nominal power
    // or the storage overall discharge power by more than 10%, a warning is shown in the form
    formStep.model.inverter.inverterOverallActivePowerWarning.disabled =
        isPluginGeneratingPlant(formStep.formModel) ||
        !isPv(formStep.formModel) ||
        !isOverallActivePowerWarningEnabled(
            formStep.model.inverter.inverterOverallActivePower.value,
            getOverallNominalPower(formStep.model),
        );

    formStep.model.inverter.inverterOverallActivePower.disabled = formStep.model.inverterStorage.inverterOverallActivePower.disabled = isPluginGeneratingPlant(
        formStep.formModel,
    );

    formStep.model.inverter.inverterOverallApparentPower.disabled = formStep.model.inverterStorage.inverterOverallApparentPower.disabled = isPluginGeneratingPlant(
        formStep.formModel,
    );

    formStep.model.inverterStorage.inverterOverallActivePowerWarning.disabled = true;
};
